// categorizationApi.ts
// Multi-supplier product categorization workbench client.

export interface ProductCategory {
  id: number
  parentId: number | null
  code: string
  label: string
  sortOrder: number
  bioDefault: boolean | null
}

export interface UncategorizedArticle {
  articleKey: string
  supplierArticleKey: string | null
  itemName: string
  lineCount: number
  invoiceCount: number
  totalNet: string         // decimal as string (avoid float precision loss)
  firstSeen: string | null // ISO date
  lastSeen: string | null
}

export async function fetchProductCategories(): Promise<ProductCategory[]> {
  const res = await fetch('/api/product-categories', { credentials: 'include' })
  if (!res.ok) throw new Error(`Kategorien konnten nicht geladen werden (${res.status})`)
  const body = await res.json()
  return body.categories ?? []
}

export async function fetchUncategorizedArticles(supplierId: string): Promise<UncategorizedArticle[]> {
  const res = await fetch(
    `/api/suppliers/${encodeURIComponent(supplierId)}/uncategorized-products`,
    { credentials: 'include' },
  )
  if (!res.ok) throw new Error(`Unkategorisierte Artikel konnten nicht geladen werden (${res.status})`)
  const body = await res.json()
  return body.articles ?? []
}

export interface CreateMappingRequest {
  supplierArticleKey: string
  supplierArticleIdRaw?: string | null
  itemNameNorm: string
  productCategoryId: number
  bioFlag: boolean
  source?: 'user_confirmed' | 'ai_suggested' | 'rule'
  confidence?: number | null
  validFrom?: string | null
}

export interface CategorySuggestion {
  candidates: Array<{
    categoryCode: string
    confidence: number
    reason: string
  }>
  bioFlag: {
    value: boolean
    confidence: number
    reason: string
  }
}

export async function suggestProductCategory(itemName: string, supplierHint?: string): Promise<CategorySuggestion> {
  const res = await fetch('/api/ai/category-suggestions', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemName, supplierHint }),
  })
  if (!res.ok) throw new Error(`Vorschlag fehlgeschlagen (${res.status})`)
  return res.json()
}

export async function createSupplierProductMapping(
  supplierId: string,
  body: CreateMappingRequest,
): Promise<{ supplierProductMappingId: number }> {
  const res = await fetch(`/api/suppliers/${encodeURIComponent(supplierId)}/product-mappings`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const eb = await res.json().catch(() => ({}))
    throw new Error(eb.error ?? `Zuordnung fehlgeschlagen (${res.status})`)
  }
  return res.json()
}
